import { Modal, Tag, Typography } from 'antd';
import { FC, ReactNode } from 'react';

import styles from '../OperationsDashboard/ops-theme.module.css';

const { Text } = Typography;

// Ops design tokens — modal renders outside .opsTheme scope so tokens are explicit
const DARK = {
  bgBase: '#0C0612',
  bgSurface: '#130D1E',
  bgElevated: '#1A1228',
  borderDefault: '#2D1F4A',
  borderSubtle: '#1E1530',
  textPrimary: '#ffffff',
  textSecondary: '#F0EBF8',
  textMuted: '#9B8DB8',
  textInverse: '#0C0612',
  magentaGhost: 'rgba(232, 0, 138, 0.08)',
  fontDisplay: "'Barlow Condensed', sans-serif",
  fontMono: "'DM Mono', monospace",
};

const LIGHT = {
  bgBase: '#F5F2FA',
  bgSurface: '#FFFFFF',
  bgElevated: '#F0EBF8',
  borderDefault: '#C9BBE0',
  borderSubtle: '#E0D6F0',
  textPrimary: '#0C0612',
  textSecondary: '#2E1548',
  textMuted: '#6B5C82',
  textInverse: '#ffffff',
  magentaGhost: 'rgba(91, 45, 142, 0.06)',
  fontDisplay: "'Barlow Condensed', sans-serif",
  fontMono: "'DM Mono', monospace",
};

export interface IOpsMetricDetailRow {
  label: string;
  value: string | number;
  indent?: boolean;
  bold?: boolean;
  color?: string;
}

export interface IOpsMetricDetailConfig {
  title: string;
  description: ReactNode;
  /** First row is used as the headline (large value + badge) when it has a `color` */
  rows: IOpsMetricDetailRow[];
  formula?: string;
  /** Badge label shown next to headline value, e.g. "Good" / "Warning" / "Action Required" */
  statusLabel?: string;
}

interface IOpsMetricDetailModalProps {
  visible: boolean;
  onClose: () => void;
  config: IOpsMetricDetailConfig | null;
  isDarkMode?: boolean;
}

type Tokens = typeof DARK;

const StatRow: FC<IOpsMetricDetailRow & { T: Tokens }> = ({
  label,
  value,
  indent,
  bold,
  color,
  T,
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: `5px ${indent ? '16px' : '0'}`,
    }}
  >
    <Text style={{ color: T.textMuted, fontSize: 13 }}>{label}</Text>
    <Text
      style={{
        color: color ?? T.textPrimary,
        fontSize: 13,
        fontWeight: bold ? 700 : 400,
      }}
    >
      {value}
    </Text>
  </div>
);

const OpsMetricDetailModal: FC<IOpsMetricDetailModalProps> = ({
  visible,
  onClose,
  config,
  isDarkMode = true,
}) => {
  if (!config) return null;

  const T = isDarkMode ? DARK : LIGHT;

  const [headlineRow, ...bodyRows] = config.rows;
  const hasHeadline = !!headlineRow?.color;

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={520}
      destroyOnClose
      wrapClassName={
        isDarkMode ? styles.opsMetricModal : styles.opsMetricModalLight
      }
      title={
        <span style={{ color: T.textPrimary, fontSize: 15, fontWeight: 700 }}>
          {config.title}
        </span>
      }
      bodyStyle={{ background: T.bgBase, padding: '16px 20px' }}
      style={{ top: 80 }}
    >
      {/* Explainer */}
      <div
        style={{
          background: T.magentaGhost,
          border: `1px solid ${T.borderDefault}`,
          borderRadius: 6,
          padding: '10px 14px',
          marginBottom: 16,
        }}
      >
        <Text style={{ color: T.textSecondary, fontSize: 12 }}>
          {config.description}
        </Text>
      </div>

      {/* Breakdown card */}
      <div
        style={{
          background: T.bgElevated,
          border: `1px solid ${T.borderDefault}`,
          borderRadius: 8,
          padding: '16px 20px',
        }}
      >
        {/* Headline — large value + optional badge */}
        {hasHeadline && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
              paddingBottom: 12,
              borderBottom: `1px solid ${T.borderSubtle}`,
            }}
          >
            <Text style={{ color: T.textMuted, fontSize: 12 }}>
              {headlineRow.label}
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: headlineRow.color,
                  lineHeight: 1,
                  fontFamily: T.fontDisplay,
                }}
              >
                {headlineRow.value}
              </span>
              {config.statusLabel && (
                <Tag
                  style={{
                    background: headlineRow.color,
                    border: 'none',
                    color: T.textInverse,
                    fontWeight: 700,
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {config.statusLabel}
                </Tag>
              )}
            </div>
          </div>
        )}

        {/* Body stat rows */}
        {(hasHeadline ? bodyRows : config.rows).map((row, i) => (
          <StatRow key={i} {...row} T={T} />
        ))}
      </div>

      {/* Formula */}
      {config.formula && (
        <div
          style={{
            background: T.bgSurface,
            border: `1px solid ${T.borderDefault}`,
            borderRadius: 6,
            padding: '10px 14px',
            marginTop: 14,
            fontFamily: T.fontMono,
          }}
        >
          <Text style={{ color: T.textMuted, fontSize: 12 }}>
            {config.formula}
          </Text>
        </div>
      )}
    </Modal>
  );
};

export default OpsMetricDetailModal;
